import { EMessageType, ErrorMessagesTypes, SURPORT_MODEL_ID } from "@/constants";
import { IMainPayload, ITranscriptionUpdateTransPayload } from "@/types/audio";
import EventEmitter2 from "eventemitter2";
import Worker from "./socketWorker?worker";
import appStore from "@/store/app.store";
import { formatTextsWithTran } from './util';
export class SocketClient extends EventEmitter2 {
  private _streamName: string;
  private _wssPushUrl: string;
  private _worker: Worker;
  private _readyState: number = 0;
  private _stopSocketParams: {
    resolve: (params: unknown) => void;
    reject: (params: unknown) => void;
  } | null;

  constructor() {
    super();
    this._worker = new Worker();
    this._streamName = "";
    this._wssPushUrl = "";
    this._stopSocketParams = null;
    this._readyState = 0;
    this._initWorker();
  }

  private _initWorker = () => {
    this._worker!.onmessage = (e) => {
      const { eventName, payload } = e.data;
      switch (eventName) {
        case "onLog":
          this._onLog(JSON.parse(payload));
          break;
        case "onMessage":
          this._onMessage(payload);
          break;
        case "onClose":
          this._onClose();
          break;
        case "onError":
          this._onError(JSON.parse(payload));
          break;
        case "onStop":
          if (payload.fn === "resolve") {
            this._stopSocketParams?.resolve(payload.params);
          } else {
            this._stopSocketParams?.reject(payload.params);
          }
          break;
        case "onReadyStateChange":
          console.log('onReadyStateChange', payload);
          this._readyState = payload;
          break;
        default:
          break;
      }
    };
  };

  public start = async (streamName: string, wssPushUrl: string) => {
    if (this._readyState === 1) {
      console.log('socket already connected');
      return;
    }
    this._wssPushUrl = wssPushUrl;
    this._streamName = streamName;
    this._worker.postMessage({
      eventName: "start",
      payload: {
        streamName,
        wssPushUrl,
      },
    });
  };

  public pause = async () => {
    return new Promise((resolve, reject) => {
      this._stopSocketParams = {
        resolve,
        reject,
      };
      this._worker.postMessage({
        eventName: "stopSocket",
      });
    });
  };

  public continue = async () => {
    this.start(this._streamName, this._wssPushUrl);
  };

  public reciveData = (buffer: number[]) => {
    this._worker.postMessage({
      eventName: "pushData",
      payload: buffer,
    });
  };

  public stop = async () => {
    this._streamName = "";
    this._wssPushUrl = "";
    return new Promise((resolve, reject) => {
      this._stopSocketParams = {
        resolve,
        reject,
      };
      this._worker.postMessage({
        eventName: "stopSocket",
      });
    });
  };

  public getReadyState = () => {
    return new Promise((resolve) => {
      this._worker.postMessage({
        eventName: "getReadyState",
      });
      setTimeout(() => {
        resolve(this._readyState);
      }, 1000);
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _onLog = (logInfo: any) => {
    const { type, message, isConsoleAccordingStorage = false } = logInfo;
    if (!isConsoleAccordingStorage || localStorage.getItem("meetingSummaryLog")) {
      console[type as "log" | "error"](...message);
    }
  }

  private _onMessage = (data: IMainPayload | ITranscriptionUpdateTransPayload) => {
    const { type } = data;
    const asrModelId = appStore.getState().asrModelId;

    if (localStorage.getItem("meetingSummaryLog")) {
      console.log("_onMessage", data);
    }

    if (type === EMessageType.TranscriptionResultChange) {
      const textsWithTran = formatTextsWithTran(data.body.pg, data.body.transPg);
      this.emit("message", { texts: textsWithTran, status: "unstable" });
    } else if (type === EMessageType.TranscriptionComplete) {
      const textsWithTran = formatTextsWithTran(data.body.pg, data.body.transPg);
      this.emit("message", { texts: textsWithTran, status: "stable" });
    } else if (type === EMessageType.TranscriptionWithSpeakerName || type === EMessageType.TranscriptionSupplement) {
      const status = type === EMessageType.TranscriptionWithSpeakerName ? "completeWithSpeakerName" : "transcriptionSupplement";
      const textsWithTran = formatTextsWithTran(data.body.pg, data.body.transPg);
      // 通义听悟版本不返回speakerName字段，为了兼容我们的数据格式，添加上该字段
      if (asrModelId === SURPORT_MODEL_ID.TingwuSaas) {
        const formatPg = textsWithTran.map(item => {
          item.speakerName = [item.roleName || `发言人${item.role}`];
          return item;
        })
        this.emit("message", { texts: formatPg, status, originalData: data.body });
      } else {
        this.emit("message", { texts: textsWithTran, status, originalData: data.body });
      }
    } else if (type === EMessageType.TranscriptionCorrect) {
      const textsWithTran = formatTextsWithTran(data.body.pg, data.body.transPg);
      const formatPg = textsWithTran.map(item => {
        item.sc = item.sc.map(sItem => {
          const { bt, et, si, tc } = sItem;
          sItem.words = [
            {
              word: tc,
              bt,
              et,
              wi: `w-${si}`,
              mark: false,
            },
          ];
          return sItem;
        })
        return item;
      })
      this.emit("message", { texts: formatPg, status: "correct" });
    } else if (type === EMessageType.SmartChapter) {
      this.emit("message", { texts: data, status: "smartChapter" });
    } else if (type === EMessageType.SmartChapterKeyframe) {
      this.emit("message", { texts: data, status: "smartChapterKeyframe" });
    } else if (type === EMessageType.TranscriptionUpdateTrans || type === EMessageType.PersonalModify) {
      console.log('TranscriptionUpdateTrans or PersonalModify >>>> ', data);
      const { editNote, optEventType } = data.body as ITranscriptionUpdateTransPayload['body'];
      if (!editNote) return;
      const { pg, transPg } = editNote;
      if (!pg || !transPg) return;
      if (optEventType === 'UPDATE') {
        const formatPg = formatTextsWithTran(pg, transPg);
        this.emit("message", { texts: formatPg, status: "transcriptionUpdateTrans" });
      }
    } else if (type === EMessageType.PersonalModifyRole) {
      this.emit("message", { originalData: data.body, status: "personalModifyRole" });
    } else if (type === EMessageType.End) {
      this.emit("end", { status: "end" });
    } else if (ErrorMessagesTypes.includes(type)) {
      this.emit("error", { code: type, message: "" });
    }
  };

  private _onClose = () => {
    this.emit("close");
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _onError = (error: any) => {
    this.emit("error", error);
  };
}
